import { CreateButton, useTable } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Space, Table, Tooltip } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { dbCols, UserRole } from '@cc/schema';
import { format } from 'date-fns';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { Link, useNavigate } from 'react-router-dom';
import { EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { SupportingCycle } from '../../schema/be-graphql-generated';
import { generatePlantUmlText } from '@cc/utils';
import { DiagramImage } from '../../components/DiagramImage';
import { useContext, useEffect, useState } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const SupportingCyclesList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const locale = useGetLocale();

  const currentLocale = locale();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const { tableProps, setFilters } = useTable<SupportingCycle>({
    // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
    resource: 'supportingCycles',
    queryOptions: {
      select: (data) => {
        // L12 finding, fixed at build (founder G1 2026-08-29 `portal-list-dedupe`): each cycle
        // rendered TWICE. The practice-scoped query can return one row per (client, therapist)
        // permission edge, so the table de-dupes by `_id` before it renders.
        const seen = new Set<string>();
        // @ts-ignore
        const rows = (data.data.data as SupportingCycle[]).filter((d) => {
          if (!d?._id || seen.has(d._id)) return false;
          seen.add(d._id);
          return true;
        });
        return { data: rows, total: data.total };
      },
    },

    meta: {
      operation: 'supportingCycles',
      fields: [
        {
          data: [
            '_id',
            'userId',
            'createdAt',
            'updatedAt',
            'name',
            {
              thoughts: ['thought', 'edges'],
            },
          ],
        },
        'count',
      ],
    },

    filters: {
      initial: clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
    },

    sorters: {
      initial: [
        {
          field: 'updatedAt',
          order: 'desc',
        },
      ],
    },
  });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  const expandedRowRender = (record: SupportingCycle) => {
    // @ts-ignore
    const umlText = generatePlantUmlText(record);

    return (
      <div>
        {umlText ? (
          <DiagramImage
            source={umlText}
            fallbackLines={[
              record.name ?? '',
              ...(record.thoughts ?? []).map(
                (thought) => thought?.thought ?? '',
              ),
            ]}
          />
        ) : null}
      </div>
    );
  };

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  useActivityTracking(dbCols.supportingCycles, fixedClientId, !fixedClientId);

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('pages.supportingCycle.supportingCycles')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-supporting-cycles-toggle-names"
            />
          )}
          <Button
            icon={<PlusCircleOutlined />}
            onClick={() => {
              // @ts-ignore
              setExpandedRowKeys(tableProps?.dataSource?.map((d) => d._id));
            }}
          >
            {translate('pages.supportingCycle.expandAll')}
          </Button>
          <CreateButton
            data-testid="create-supporting-cycle-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/supportingCycles/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
        </div>
      }
    >
      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record._id]);
            } else {
              setExpandedRowKeys([
                ...expandedRowKeys.filter((k) => k !== record._id),
              ]);
            }
          },
        }}
        data-testid="supporting-cycles-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('resourcesCommon.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: SupportingCycle) => {
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}
        <Table.Column
          key="name"
          dataIndex="name"
          title={translate('pages.supportingCycle.cycleName')}
        />
        <Table.Column
          key="updatedAt"
          dataIndex="updatedAt"
          title={translate('resourcesCommon.updatedAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss a',
            );
          }}
        />
        <Table.Column<SupportingCycle>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => {
            const client = clients.find((c) => c._id === record.userId);

            const hasPremiumAccess = client?.accessLevel;

            const canEdit =
              client?.access?.allowEdit ||
              (client?.access?.allowAdminEdit &&
                identity?.db?.role === UserRole.THERAPIST_ADMIN);

            return (
              <Space>
                {canEdit && (
                  <Tooltip
                    title={translate(
                      hasPremiumAccess
                        ? 'resourcesCommon.edit'
                        : 'clients.thoughtsTestDisabledTip',
                    )}
                  >
                    <Link
                      to={`/tools/supportingCycles/create?cycleId=${record._id}`}
                    >
                      <Button
                        icon={<EditOutlined />}
                        disabled={!hasPremiumAccess || !canEdit}
                      />
                    </Link>
                  </Tooltip>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
